import type {ILocationType} from '../types/ILocationType';
import type {TranslationFunction} from 'src/types/language-support.ts';
import type {IGetAvailabilityRangeResponse} from '../types/dto/IGetAvailabilityRangeResponse';
import type {ITimeSlot} from '../types/ITimeSlot';
import type {ITimeSlots} from '../types/ITimeSlots';
import type {SequenceDay} from '../machines/bookingMachine/bookingMachine.types';
import type {SequenceItem} from '../types/IEventTemplateMetadata';

import {
  addDays,
  isTZAfter,
  isTZEqual,
  isTZBefore,
  createTZDate,
  addTZDays,
  subTZDays,
  formatWithIntl,
  startOfTZDay,
} from './date';
import {captureSentryMessage} from 'src/utils/sentry';
import {isValidTimezone} from 'src/components/scheduler/lib/timezone-picker/utils';


export const getMeetLinkProviders = (
  t: TranslationFunction,
): Record<string, string> => ({
  google_meet: t('Google_Meet', 'Google Meet'),
  zoom: t('Zoom', 'Zoom'),
  microsoft_teams: t('Microsoft_Teams', 'Microsoft Teams'),
  webex: t('Webex', 'Webex'),
});

export const getLocationTitleLabelMap = (
  t: TranslationFunction,
): Record<
  ILocationType,
  {
    label?: string;
    icon: string;
  }
> => ({
  custom_location: {
    label: t('Custom_Location', 'Custom Location'),
    icon: 'map-pin',
  },
  phone_number: {
    label: t('Phone_Meeting', 'Phone Meeting'),
    icon: 'phone',
  },
  meeting_link: {
    icon: 'video',
  },
});

export const canNavigatePreviousWeek = (
  timezone: string,
  selectedStartDateOfWeek: Date,
  availabilityRange: IGetAvailabilityRangeResponse,
  currentSequenceDay?: number,
): boolean => {
  if (currentSequenceDay && currentSequenceDay > 0) {
    return false;
  }
  const rangeStartDate = createTZDate(
    availabilityRange.start_at * 1000,
    timezone,
  );
  const prevWeekStart = subTZDays(selectedStartDateOfWeek, 7, timezone);
  return (
    isTZAfter(prevWeekStart, rangeStartDate, timezone) ||
    isTZEqual(prevWeekStart, rangeStartDate, timezone)
  );
};

/**
 * True if any slot in timeSlots falls on a day before or on the normal range end day.
 */
export const hasSlotsInNormalRange = (
  timeSlots: ITimeSlots | undefined,
  normalRangeEndTimestampSeconds: number,
  timezone: string,
): boolean => {
  const normalRangeEndDate = createTZDate(
    normalRangeEndTimestampSeconds * 1000,
    timezone,
  );
  const normalRangeEndStartOfDay = startOfTZDay(normalRangeEndDate, timezone);

  const slots = timeSlots?.free_time_slots ?? {};
  return Object.keys(slots).some((dateKey) => {
    const slotList = slots[dateKey] ?? [];
    if (!slotList.length) {
      return false;
    }
    const slotStartOfDay = startOfTZDay(dateKey, timezone);
    return isTZBefore(slotStartOfDay, normalRangeEndStartOfDay, timezone);
  });
};

export const canNavigateNextWeek = ({
  timezone,
  selectedStartDateOfWeek,
  availabilityRange,
  hasNoSlotsInNormalRange,
  currentSequenceDay,
  showExtendedAvailabilityByDefault,
}: {
  timezone: string;
  selectedStartDateOfWeek: Date;
  availabilityRange: IGetAvailabilityRangeResponse;
  hasNoSlotsInNormalRange: boolean;
  currentSequenceDay?: number;
  showExtendedAvailabilityByDefault: boolean;
}): boolean => {
  if (currentSequenceDay && currentSequenceDay > 0) {
    return false;
  }

  const currentWeekEndDate = addTZDays(selectedStartDateOfWeek, 6, timezone);
  const normalRangeEndDate = createTZDate(
    (availabilityRange.end_at ?? 0) * 1000,
    timezone,
  );

  /**
   * Use extended range end if:
   * 1. Extended end at is available
   * 2. There are no slots in normal range
   * 3. Current week's last date is after or equal to normal range end date (means user has crossed normal range)
   */
  const userHasCrossedNormalRange =
    isTZEqual(currentWeekEndDate, normalRangeEndDate, timezone) ||
    isTZAfter(currentWeekEndDate, normalRangeEndDate, timezone);

  const useExtendedRangeEnd =
    Boolean(availabilityRange.extended_end_at) &&
    ((hasNoSlotsInNormalRange && userHasCrossedNormalRange) ||
      showExtendedAvailabilityByDefault);

  const rangeEndTimestamp = useExtendedRangeEnd
    ? (availabilityRange.extended_end_at ?? 0) * 1000
    : availabilityRange.end_at * 1000;
  const rangeEndDate = createTZDate(rangeEndTimestamp, timezone);
  const nextWeekStartDate = addTZDays(selectedStartDateOfWeek, 7, timezone);

  return (
    isTZBefore(nextWeekStartDate, rangeEndDate, timezone) ||
    isTZEqual(nextWeekStartDate, rangeEndDate, timezone)
  );
};

export const formatAvailabilityTime = (
  time: string,
  language: string,
): string => {
  // Parse time string (e.g., "15:30") to extract hours and minutes
  const [hours, minutes] = time.split(':').map(Number);

  // Create a Date object with today's date but with the specified time
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  // Use Intl.DateTimeFormat directly
  return formatWithIntl(date, language, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const isTimezoneSupported = (timezone: string) => {
  if (!isValidTimezone(timezone)) {
    captureSentryMessage(`Invalid timezone: ${timezone}`, {
      level: 'error',
      tags: {product: 'scheduler', timezone},
    });
    return false;
  }
  return true;
};

export function getWeekDates(startOfWeek: Date) {
  return Array.from({length: 7}, (_, i) => addDays(startOfWeek, i));
}

export const getSelectedStartDateOfWeekFromSequenceDays = (
  sequenceDays: SequenceDay[] | undefined,
  sequenceNumber: number,
): Date | undefined => {
  return sequenceDays?.find((day) => day.sequenceNumber === sequenceNumber)
    ?.selectedStartDateOfWeek;
};

export const getSelectedTimeslots = (
  sequenceDays: SequenceDay[] | undefined,
): Array<{day: number; date: string; start_time: string}> => {
  if (!sequenceDays) {
    return [];
  }

  return sequenceDays
    .filter((day) => day.selectedDate && day.selectedTimeSlot?.start_time)
    .map((day) => ({
      day: day.sequenceNumber,
      date: day.selectedDate!,
      start_time: day.selectedTimeSlot!.start_time,
    }));
};

export const generateSequenceDaysFromDayZeroDateChange = ({
  dayZeroSequenceDay,
  sequence,
  timezone,
}: {
  dayZeroSequenceDay: SequenceDay;
  sequence: SequenceItem[] | undefined;
  timezone: string;
}): SequenceDay[] => {
  // Start with day 0 (keep its existing selectedStartDateOfWeek)
  const updatedSequenceDays: SequenceDay[] = [dayZeroSequenceDay];

  // If no sequence, return just day 0
  if (!sequence || sequence.length === 0) {
    return updatedSequenceDays;
  }

  // Parse day 0's selectedDate to calculate start of week for other days
  if (!dayZeroSequenceDay.selectedDate) {
    return updatedSequenceDays;
  }

  let currentStartDate = createTZDate(
    `${dayZeroSequenceDay.selectedDate}T00:00:00`,
    timezone,
  );
  let sequenceNumber = 1; // Start from day 1 since day 0 is already handled

  for (let i = 1; i < sequence.length; i++) {
    const item = sequence[i];

    if (item.type === 'day') {
      // Create new day with selectedStartDateOfWeek calculated from day 0's selectedDate and gaps
      updatedSequenceDays.push({
        sequenceNumber,
        selectedDate: undefined,
        selectedTimeSlot: undefined,
        timeSlots: undefined,
        selectedStartDateOfWeek: currentStartDate,
      });

      sequenceNumber++;
    } else if (item.type === 'gap' && item.value) {
      const gapValue = item.value as {amount?: number; unit?: string};
      if (gapValue.unit === 'days' && gapValue.amount) {
        // Add gap amount and subtract 1 day as per requirement
        currentStartDate = addTZDays(
          currentStartDate,
          gapValue.amount - 1,
          timezone,
        );
      }
    }
  }

  return updatedSequenceDays;
};

export const generateSequenceDaysFromAnyDayDateChange = ({
  changedDaySequenceNumber,
  newDate,
  existingSequenceDays = [],
  sequence,
  timezone,
}: {
  changedDaySequenceNumber: number;
  newDate: string;
  existingSequenceDays: SequenceDay[];
  sequence: SequenceItem[] | undefined;
  timezone: string;
}): SequenceDay[] => {
  // If no sequence, return existing days with just the changed day updated
  if (!sequence || sequence.length === 0) {
    return updateSequenceDays(existingSequenceDays, changedDaySequenceNumber, {
      selectedDate: newDate,
      selectedTimeSlot: undefined,
    });
  }

  // Parse the new date
  const changedDate = createTZDate(`${newDate}T00:00:00`, timezone);

  // Keep all days before the changed day unchanged
  const updatedSequenceDays: SequenceDay[] = existingSequenceDays
    .filter((day) => day.sequenceNumber < changedDaySequenceNumber)
    .map((day) => ({...day}));

  // Update the changed day
  const changedDay = existingSequenceDays.find(
    (day) => day.sequenceNumber === changedDaySequenceNumber,
  );
  updatedSequenceDays.push({
    ...(changedDay ?? {}),
    selectedDate: newDate,
    sequenceNumber: changedDaySequenceNumber,
    selectedTimeSlot: undefined, // Clear time slot when date changes
  });

  // Calculate subsequent days based on the changed day's new date
  let currentStartDate = changedDate;
  let sequenceNumber = changedDaySequenceNumber + 1;

  // Find the index in sequence array where the changed day is located
  // Count only 'day' items to match sequence numbers
  let dayCount = 0;
  let sequenceIndex = -1;
  for (let i = 0; i < sequence.length; i++) {
    if (sequence[i].type === 'day') {
      if (dayCount === changedDaySequenceNumber) {
        sequenceIndex = i;
        break;
      }
      dayCount++;
    }
  }

  // If we couldn't find the sequence index, return what we have
  if (sequenceIndex === -1) {
    return updatedSequenceDays;
  }

  // Process sequence items after the changed day
  // First, handle any gap that might be after the changed day
  let nextItemIndex = sequenceIndex + 1;
  if (
    nextItemIndex < sequence.length &&
    sequence[nextItemIndex].type === 'gap'
  ) {
    const gapItem = sequence[nextItemIndex];
    if (gapItem.value) {
      const gapValue = gapItem.value as {amount?: number; unit?: string};
      if (gapValue.unit === 'days' && gapValue.amount) {
        // Add gap amount and subtract 1 day as per requirement
        currentStartDate = addTZDays(
          currentStartDate,
          gapValue.amount - 1,
          timezone,
        );
      }
    }
    nextItemIndex++;
  }

  // Now process remaining days
  for (let i = nextItemIndex; i < sequence.length; i++) {
    const item = sequence[i];

    if (item.type === 'day') {
      // Create new day with selectedStartDateOfWeek calculated from previous day + gap
      updatedSequenceDays.push({
        sequenceNumber,
        selectedDate: undefined,
        selectedTimeSlot: undefined,
        timeSlots: undefined,
        selectedStartDateOfWeek: currentStartDate,
      });

      sequenceNumber++;
    } else if (item.type === 'gap' && item.value) {
      const gapValue = item.value as {amount?: number; unit?: string};
      if (gapValue.unit === 'days' && gapValue.amount) {
        // Add gap amount and subtract 1 day as per requirement
        currentStartDate = addTZDays(
          currentStartDate,
          gapValue.amount - 1,
          timezone,
        );
      }
    }
  }

  return updatedSequenceDays;
};

export const updateSequenceDays = (
  currentSequenceDays: SequenceDay[] | undefined,
  sequenceNumber: number,
  updates: {
    selectedDate?: string;
    selectedTimeSlot?: ITimeSlot | undefined;
    timeSlots?: ITimeSlots;
    selectedStartDateOfWeek?: Date;
  },
): SequenceDay[] => {
  const currentSequenceArray = currentSequenceDays || [];
  const existingIndex = currentSequenceArray.findIndex(
    (day) => day.sequenceNumber === sequenceNumber,
  );

  if (existingIndex !== -1) {
    // Update existing entry
    const newSequenceArray = [...currentSequenceArray];
    const existingDay = newSequenceArray[existingIndex];

    newSequenceArray[existingIndex] = {
      sequenceNumber,
      selectedDate:
        'selectedDate' in updates
          ? updates.selectedDate
          : existingDay.selectedDate ?? undefined,
      selectedTimeSlot:
        'selectedTimeSlot' in updates
          ? updates.selectedTimeSlot
          : existingDay.selectedTimeSlot ?? undefined,
      timeSlots:
        'timeSlots' in updates ? updates.timeSlots : existingDay.timeSlots,
      selectedStartDateOfWeek:
        'selectedStartDateOfWeek' in updates
          ? updates.selectedStartDateOfWeek
          : existingDay.selectedStartDateOfWeek,
    };
    return newSequenceArray;
  }

  // Add new entry
  return [
    ...currentSequenceArray,
    {
      sequenceNumber,
      selectedDate: updates.selectedDate ?? undefined,
      selectedTimeSlot: updates.selectedTimeSlot ?? undefined,
      timeSlots: updates.timeSlots,
      selectedStartDateOfWeek: updates.selectedStartDateOfWeek,
    },
  ];
};
